import { Link } from 'react-router-dom';
import { site } from '../config/site';

export type LegalKind = 'privacy' | 'terms' | 'support';

const META: Record<
  LegalKind,
  { title: string; heading: string; description: string }
> = {
  privacy: {
    title: 'Privacy Policy · MindHeart',
    heading: 'Privacy Policy',
    description: 'How MindHeart collects and uses your information.',
  },
  terms: {
    title: 'Terms of Use · MindHeart',
    heading: 'Terms of Use',
    description: 'Terms for using the MindHeart relationship intelligence app.',
  },
  support: {
    title: 'Support · MindHeart',
    heading: 'Support',
    description: 'Get help with MindHeart subscriptions, trial, and account.',
  },
};

function PrivacyBody() {
  return (
    <>
      <h2>Who we are</h2>
      <p>
        MindHeart is an AI relationship intelligence app operated by{' '}
        <strong>Omnivatis</strong> (“we”, “us”). Contact:{' '}
        <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>.
      </p>

      <h2>Information we collect</h2>
      <p>
        The categories below match what we declare in the Apple App Store and
        Google Play data-safety forms.
      </p>
      <ul>
        <li>
          <strong>Contact info</strong> — email address and name for your
          account.
        </li>
        <li>
          <strong>User content</strong> — your birth profile (date, optional
          time/place); connection nicknames and relationship types; optional
          birth data you enter for others; coach chat messages; compatibility
          reports generated for you.
        </li>
        <li>
          <strong>Purchases</strong> — subscription product and entitlement
          dates from Apple App Store / Google Play (we do not store full payment
          card numbers).
        </li>
        <li>
          <strong>Identifiers</strong> — account user ID; device / push token
          identifiers used for Firebase Cloud Messaging and analytics.
        </li>
        <li>
          <strong>Usage data</strong> — product interaction events (sign-in,
          report generation, coach use, paywall views, restore/purchase) via
          Firebase Analytics.
        </li>
        <li>
          <strong>Diagnostics</strong> — crash and performance data via Firebase
          Crashlytics to keep the app reliable.
        </li>
      </ul>

      <h2>How we use information</h2>
      <ul>
        <li>
          <strong>App functionality</strong> — authenticate you, generate
          compatibility insights, power the relationship coach, sync history,
          enforce free-trial and subscription limits, and send optional product
          notifications when you allow them.
        </li>
        <li>
          <strong>Analytics &amp; reliability</strong> — understand feature use
          and fix crashes.
        </li>
      </ul>

      <h2>Third-party birth data</h2>
      <p>
        When you add a connection, you should only enter information you have a
        right to use (for example your own perspective, or details shared with
        permission). Prefer nicknames over full legal names. Deleting a
        connection removes associated reports and coach memory for that person.
      </p>

      <h2>AI processing</h2>
      <p>
        Structured chart data and your questions may be sent to our AI providers
        (currently OpenAI) to generate plain-language reports and coach replies.
        Do not paste highly sensitive third-party content you are not allowed to
        share.
      </p>

      <h2>Sharing</h2>
      <p>
        We do not sell your personal information. We share data with processors
        needed to run MindHeart (hosting, AI, analytics, push, app stores) under
        appropriate agreements.
      </p>

      <h2>Retention &amp; deletion</h2>
      <p>
        We keep account and relationship data while your account is active. You
        can delete a connection in-app, or delete your account from Profile →
        Settings (or email us from your registered address). Store subscriptions
        must be canceled separately in Apple / Google settings.
      </p>

      <h2>Children</h2>
      <p>
        MindHeart is not directed to children under 13 (or the minimum age
        required in your region). Do not create an account for a child.
      </p>

      <h2>Changes</h2>
      <p>
        We may update this policy. The “Last updated” date above will change when
        we do. Continued use after changes means you accept the updated policy.
      </p>
      <p className="cta-row">
        <Link className="cta" to={site.termsUrl}>
          Terms of Use
        </Link>
        <Link className="cta" to={site.supportUrl}>
          Support
        </Link>
      </p>
    </>
  );
}

function TermsBody() {
  return (
    <>
      <h2>Agreement</h2>
      <p>
        By downloading or using MindHeart you agree to these Terms of Use and our{' '}
        <Link to={site.privacyUrl}>Privacy Policy</Link>. If you do not agree,
        do not use the app.
      </p>
      <h2>The service</h2>
      <p>
        MindHeart provides AI-assisted relationship insights, compatibility
        reports, and coaching. Content is for reflection and communication
        support — not medical, psychological, legal, or financial advice, and
        not a guarantee of relationship outcomes.
      </p>
      <h2>Accounts</h2>
      <p>
        You must provide accurate registration details and keep your credentials
        secure. You are responsible for activity under your account and for
        information you enter about other people.
      </p>
      <h2>Subscriptions &amp; trials</h2>
      <ul>
        <li>
          New users may receive a limited free trial (connections, reports, and
          coach allowance).
        </li>
        <li>
          <strong>Premium</strong> and <strong>Pro Max</strong> are
          auto-renewable <strong>monthly</strong> subscriptions billed through
          the Apple App Store or Google Play.
        </li>
        <li>
          Payment is charged to your Apple ID or Google Play account at
          confirmation of purchase.
        </li>
        <li>
          Subscriptions renew automatically unless canceled at least 24 hours
          before the end of the current period.
        </li>
        <li>Prices shown in the store are authoritative for your region.</li>
        <li>
          Manage or cancel anytime in your store account settings. Deleting the
          app or your MindHeart account does not cancel a store subscription.
        </li>
      </ul>
      <h2>Acceptable use</h2>
      <p>
        Do not misuse MindHeart — including harassment, stalking facilitation,
        abusive content, attempts to disrupt the service, or unlawful activity.
        We may suspend accounts that violate these terms.
      </p>
      <h2>AI outputs</h2>
      <p>
        Reports and coach replies are generated automatically and may be
        imperfect. Use your judgment; MindHeart is not liable for decisions made
        solely on AI output.
      </p>
      <h2>Intellectual property</h2>
      <p>
        MindHeart branding, software, and materials belong to Omnivatis or its
        licensors. You retain rights to content you submit, and grant us a
        license to process it to provide the service.
      </p>
      <h2>Disclaimer</h2>
      <p>
        The service is provided “as is” to the fullest extent permitted by law.
        We do not warrant uninterrupted or error-free operation.
      </p>
      <h2>Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, Omnivatis is not liable for
        indirect, incidental, or consequential damages arising from your use of
        MindHeart.
      </p>
      <h2>Contact</h2>
      <p>
        <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>
      </p>
      <p className="cta-row">
        <Link className="cta" to={site.privacyUrl}>
          Privacy Policy
        </Link>
        <Link className="cta" to={site.supportUrl}>
          Support
        </Link>
      </p>
    </>
  );
}

function SupportBody() {
  return (
    <>
      <p>
        Need help with MindHeart? Start here or email{' '}
        <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>.
      </p>
      <div className="support-topic">
        <strong>Free trial</strong>
        <span>
          New accounts get limited free connections, reports, and coach turns.
          Subscribe for unlimited depth.
        </span>
      </div>
      <div className="support-topic">
        <strong>Subscription not unlocking</strong>
        <span>
          Use Restore purchases on the Plan screen. Confirm you are signed into
          the same Apple / Google account used to buy.
        </span>
      </div>
      <div className="support-topic">
        <strong>Cancel or change plan</strong>
        <span>
          Manage subscriptions in App Store or Google Play account settings —
          not inside MindHeart billing.
        </span>
      </div>
      <div className="support-topic">
        <strong>Delete a connection</strong>
        <span>
          Open the connection and remove it to wipe that person’s reports and
          coach thread from your account.
        </span>
      </div>
      <div className="support-topic">
        <strong>Delete account</strong>
        <span>
          In the app go to Profile → Settings → Delete account. Or email us from
          your registered address.
        </span>
      </div>
      <h2>Legal</h2>
      <p className="cta-row">
        <Link className="cta" to={site.privacyUrl}>
          Privacy Policy
        </Link>
        <Link className="cta" to={site.termsUrl}>
          Terms of Use
        </Link>
      </p>
    </>
  );
}

export function LegalPage({ kind }: { kind: LegalKind }) {
  const meta = META[kind];

  return (
    <main className="legal-wrap">
      <div className="page-intro">
        <div className="eyebrow">MindHeart</div>
        <h1>{meta.heading}</h1>
        <p className="updated">Last updated: July 30, 2026</p>
      </div>
      <article className="legal-article">
        {kind === 'privacy' && <PrivacyBody />}
        {kind === 'terms' && <TermsBody />}
        {kind === 'support' && <SupportBody />}
      </article>
    </main>
  );
}

export function legalDocumentTitle(kind: LegalKind): string {
  return META[kind].title;
}
